import {
    AddBookmarkRequest,
    AddBookmarkRequest_ReverseAction,
    DeleteBookmarkRequest,
    ReadingListType,
} from '@app/_proto/Protos/bookmarks';
import { BookmarksClient } from '@app/_proto/Protos/bookmarks.client';
import type { ChapterItem, GetChapterResponse } from '@app/_proto/Protos/chapters';
import { BoolValue } from '@app/_proto/google/protobuf/wrappers';
import { ReactComponent as StarIcon } from '@app/assets/star.svg';
import { useAuth } from '@app/libs/auth';
import { useHttp } from '@app/libs/http';
import { CheckCircle, Close } from '@mui/icons-material';
import { Button, CircularProgress, IconButton, Snackbar } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import produce from 'immer';
import * as React from 'react';
import { startTransition, useCallback, useEffect, useState } from 'react';
//#region : Global constants and types

/** Custom. Used to mark bookmark state */
type BookmarkState =
    | 'idle'
    | 'bookmarking'
    | 'bookmarked'
    | 'existing-bookmark'
    | 'unbookmarked'
    | 'undo'
    | 'deleted'
    | 'error';

//#endregion : Global constants and types

//#region : Main component

/** Custom. Main component parameters type */
export type ChapterBookmarkFavoriteProps = {
    chapter: ChapterItem;
};

/** Custom. Manually Bookmark a given chapter as favorite and show message. */
export default function ChapterBookmarkFavorite({ chapter }: ChapterBookmarkFavoriteProps) {
    //#region : Variables, functions and api calls

    //General variables
    const { user } = useAuth();
    const { grpcRequest } = useHttp();
    const queryClient = useQueryClient();

    const [notifOpen, setNotifOpen] = useState(false);
    const [bookmarkState, setBookmarkState] = useState<BookmarkState>(
        chapter.relatedUserInfo?.isChapterFavorite?.value === true ? 'existing-bookmark' : 'idle'
    );

    //#region : Bookmark actions
    useEffect(() => {
        startTransition(() => {
            setBookmarkState(chapter.relatedUserInfo?.isChapterFavorite?.value === true ? 'existing-bookmark' : 'idle');
        });
    }, [chapter.entityId, chapter.relatedUserInfo?.isChapterFavorite?.value]);

    /**
     * Custom.
     *
     * Makes the server call to set the bookmark.
     */
    const setBookmark = useCallback(
        async (id: number) => {
            const request = AddBookmarkRequest.create({
                chapterId: id,
                listType: ReadingListType.Manual,
                reverseAction: AddBookmarkRequest_ReverseAction.Replace,
            });

            return await grpcRequest(BookmarksClient, c => c.addBookmark, request);
        },
        [grpcRequest]
    );

    /**
     * Custom.
     *
     * Makes the server call to delete the bookmark.
     */
    const deleteBookmark = useCallback(
        async (id: number) => {
            const request = DeleteBookmarkRequest.create({
                id: {
                    oneofKind: 'novelId',
                    novelId: id,
                },
                listType: ReadingListType.Manual,
            });

            return await grpcRequest(BookmarksClient, c => c.deleteBookmark, request);
        },
        [grpcRequest]
    );

    /**
     * Custom.
     *
     * Handles delete button click.
     */
    const handleUndoBookmark = useCallback(async () => {
        if (!chapter) {
            return;
        }

        try {
            if (bookmarkState === 'bookmarked') {
                await deleteBookmark(chapter.novelId);

                setBookmarkState('deleted');
            } else {
                await setBookmark(chapter.entityId);

                setBookmarkState('bookmarked');
            }

            setNotifOpen(false);
        } catch (e) {
            setBookmarkState('error');

            throw e;
        }
    }, [bookmarkState, chapter, deleteBookmark, setBookmark]);

    /**
     * Custom.
     *
     * Bookmarks the chapter and sets the bookmark state.
     */
    const doBookmark = useCallback(
        async (id: number) => {
            try {
                const response = await setBookmark(id);

                const isBookmarked = !response.previousBookmark;

                if (response.result) {
                    if (isBookmarked) {
                        setBookmarkState('bookmarked');
                    } else {
                        setBookmarkState('unbookmarked');
                    }

                    queryClient.invalidateQueries(['bookmarks', user?.id, ReadingListType.Manual]);
                } else {
                    //Chapter was already bookmarked.
                    setBookmarkState('bookmarked');
                }

                queryClient.setQueryData(
                    ['chapter', chapter.novelInfo?.slug, chapter.slug, user?.id],
                    (updater: GetChapterResponse) =>
                        produce(updater, prev => {
                            if (prev.item?.relatedUserInfo) {
                                prev.item.relatedUserInfo.isChapterFavorite = BoolValue.create({
                                    value: isBookmarked,
                                });
                            }
                        })
                );
            } catch (e) {
                setBookmarkState('error');

                throw e;
            }
        },
        [chapter.novelInfo?.slug, chapter.slug, queryClient, setBookmark, user?.id]
    );

    const onClickBookmark = useCallback(() => {
        if (!chapter) {
            return;
        }
        setBookmarkState('bookmarking');
        doBookmark(chapter.entityId);
    }, [chapter, doBookmark]);
    //#endregion : Bookmark actions

    //#region : Notification actions

    //Open up the bookmark alert when the chapter is successfully bookmarked.
    useEffect(() => {
        if (bookmarkState === 'bookmarked' || bookmarkState === 'unbookmarked') {
            startTransition(() => setNotifOpen(true));
        }
    }, [bookmarkState]);

    /**
     * Custom.
     *
     * Closes the bookmark notification.
     */
    const handleCloseNotif = useCallback(() => {
        setNotifOpen(false);
    }, []);

    //#endregion : Notification actions

    //#endregion : Variables, functions and api calls

    return (
        <React.Fragment>
            {!!chapter && (
                <div>
                    <Button
                        disabled={bookmarkState === 'bookmarking'}
                        variant="text"
                        className={clsx(
                            'group !bg-transparent pl-0 !font-semibold text-[#444] !shadow-none dark:text-white',
                            {
                                'hover:!text-blue':
                                    bookmarkState !== 'bookmarked' && bookmarkState != 'existing-bookmark',
                            }
                        )}
                        onClick={onClickBookmark}
                    >
                        <span className="mr-[10px] h-[40px] w-[40px] rounded-[32px] bg-black/[.04] dark:bg-black">
                            <StarIcon
                                className={clsx('mx-auto mt-[8px] group-hover:!text-inherit', {
                                    'text-blue':
                                        bookmarkState === 'bookmarked' || bookmarkState === 'existing-bookmark',
                                    'text-red-900': bookmarkState === 'error',
                                    'text-[#999] dark:text-white':
                                        bookmarkState !== 'error' &&
                                        bookmarkState !== 'bookmarked' &&
                                        bookmarkState != 'existing-bookmark',
                                })}
                            />
                        </span>
                        Favorite
                        {bookmarkState === 'bookmarking' && (
                            <CircularProgress className="mx-[8px] text-blue-300 group-hover:!text-inherit" size={16} />
                        )}
                    </Button>
                </div>
            )}
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                // className="mb-[56px]"
                open={notifOpen}
                autoHideDuration={3000}
                onClose={handleCloseNotif}
            >
                <div className="flex h-[48px] items-center justify-between space-x-[16px] rounded-[24px] bg-[#323232] px-[16px] opacity-90 dark:bg-white sm:w-[400px] sm:space-x-0">
                    <div className="flex items-center space-x-[8px]">
                        <CheckCircle className="text-blue" />
                        <p className="text-[14px] font-semibold text-white dark:text-black">
                            {bookmarkState === 'bookmarked' || bookmarkState === 'existing-bookmark'
                                ? 'Chapter marked as favorite!'
                                : 'Chapter unmarked as favorite!'}
                        </p>
                    </div>
                    <div className="flex items-center space-x-[10px]">
                        <p
                            className="cursor-pointer text-[14px] font-semibold text-white dark:text-black"
                            onClick={handleUndoBookmark}
                        >
                            Undo
                        </p>

                        <IconButton aria-label="close" color="primary" onClick={handleCloseNotif}>
                            <Close fontSize="small" />
                        </IconButton>
                    </div>
                </div>
            </Snackbar>
        </React.Fragment>
    );
}
//#endregion : Main component
